/**
 * Fiyat Kıyas — iwasku bazında kanallar arası fiyat karşılaştırma tablosu.
 * Kanal kartları (yükle) + ülke seçici + arama/kategori filtresi + grid + CSV export.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Upload, Search, Download, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n';
import {
  apiGetChannels,
  apiGetChannelComparison,
  apiGetProductCategories,
  type ChannelInfo,
  type ComparisonRow,
} from '../../services/api';
import { logger } from '../../utils/logger';
import ChannelImportModal from './ChannelImportModal';
import UnmatchedPanel from './UnmatchedPanel';

const COUNTRIES = ['US']; // Faz 1: US. Çok-ülke fazında genişler.
const PAGE_SIZE = 100;

const PriceComparisonPage: React.FC = () => {
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [country, setCountry] = useState('US');
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);

  const [importChannel, setImportChannel] = useState<ChannelInfo | null>(null);
  const [unmatchedOpen, setUnmatchedOpen] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ch, cmp, cats] = await Promise.all([
        apiGetChannels(),
        apiGetChannelComparison(country),
        apiGetProductCategories().catch(() => [] as string[]),
      ]);
      setChannels(ch);
      setRows(cmp);
      setCategories(cats);
    } catch (e) {
      logger.error('comparison load', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  // Grid kolonları: aktif + verisi olan kanallar, sort_order'a göre
  const gridChannels = useMemo(
    () => channels.filter(c => c.is_active && Number(c.row_count) > 0).sort((a, b) => a.sort_order - b.sort_order),
    [channels],
  );

  const totalUnmatched = useMemo(
    () => channels.reduce((s, c) => s + Number(c.unmatched_count || 0), 0),
    [channels],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (categoryFilter && r.category !== categoryFilter) return false;
      if (q && !(r.iwasku.toLowerCase().includes(q) || (r.name || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, search, categoryFilter]);

  const pageRows = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => { setPage(1); }, [search, categoryFilter]);

  const exportCsv = () => {
    const cols = gridChannels.map(c => c.store_label);
    const header = ['iwasku', tr ? 'Ürün' : 'Product', tr ? 'Kategori' : 'Category', ...cols, 'Min', 'Max', tr ? 'Fark%' : 'Spread%'];
    const lines = [header.join(',')];
    for (const r of filtered) {
      const prices = gridChannels.map(c => r.prices[c.code]?.price ?? '');
      const spread = r.min_price && r.max_price && r.min_price > 0
        ? (((r.max_price - r.min_price) / r.min_price) * 100).toFixed(1)
        : '';
      const cells = [
        r.iwasku,
        `"${(r.name || '').replace(/"/g, '""')}"`,
        `"${(r.category || '').replace(/"/g, '""')}"`,
        ...prices,
        r.min_price ?? '',
        r.max_price ?? '',
        spread,
      ];
      lines.push(cells.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fiyat-kiyas-${country}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{tr ? 'Fiyat Kıyas' : 'Price Comparison'}</h2>
          <p className="text-sm text-slate-500">{tr ? 'iwasku bazında kanallar arası fiyat karşılaştırması' : 'Cross-channel price comparison by iwasku'}</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
            aria-label={tr ? 'Ülke' : 'Country'}
          >
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {totalUnmatched > 0 && (
            <button
              onClick={() => setUnmatchedOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-sm font-medium border border-amber-200"
            >
              <AlertTriangle className="w-4 h-4" />
              {tr ? 'Eşleşmeyenler' : 'Unmatched'} ({totalUnmatched})
            </button>
          )}
          <button onClick={loadAll} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Kanal kartları */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {channels.map(c => (
          <div key={c.code} className="bg-white rounded-xl shadow-sm p-4 border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-slate-800 truncate">{c.store_label}</span>
            </div>
            <div className="text-2xl font-bold text-slate-800">{Number(c.row_count).toLocaleString()}</div>
            <div className="text-xs text-slate-400">
              {Number(c.unmatched_count) > 0
                ? <span className="text-amber-600">{Number(c.unmatched_count)} {tr ? 'eşleşmeyen' : 'unmatched'}</span>
                : (c.last_captured_at ? new Date(c.last_captured_at).toLocaleDateString() : (tr ? 'veri yok' : 'no data'))}
            </div>
            <button
              onClick={() => setImportChannel(c)}
              disabled={c.parser_type === 'amazon_spapi'}
              title={c.parser_type === 'amazon_spapi' ? (tr ? 'Amazon SP-API ile beslenecek (Faz 2)' : 'Fed via SP-API (Phase 2)') : ''}
              className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed text-indigo-700 rounded-lg text-xs font-medium"
            >
              <Upload className="w-3.5 h-3.5" />
              {tr ? 'Yükle' : 'Import'}
            </button>
          </div>
        ))}
      </div>

      {/* Filtreler */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr ? 'iwasku veya ürün adı ara...' : 'Search iwasku or product...'}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white"
        >
          <option value="">{tr ? 'Tüm kategoriler' : 'All categories'}</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded-lg text-sm font-medium"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 sticky left-0 bg-slate-50">iwasku</th>
                  <th className="text-left px-4 py-3">{tr ? 'Ürün' : 'Product'}</th>
                  {gridChannels.map(c => (
                    <th key={c.code} className="text-right px-4 py-3 whitespace-nowrap">{c.store_label}</th>
                  ))}
                  <th className="text-right px-4 py-3">{tr ? 'Fark' : 'Spread'}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(r => {
                  const spread = r.min_price && r.max_price && r.min_price > 0
                    ? ((r.max_price - r.min_price) / r.min_price) * 100
                    : null;
                  return (
                    <tr key={r.iwasku} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5 font-mono text-xs text-slate-700 sticky left-0 bg-white">{r.iwasku}</td>
                      <td className="px-4 py-2.5 text-slate-600 max-w-[260px] truncate" title={r.name}>{r.name}</td>
                      {gridChannels.map(c => {
                        const cell = r.prices[c.code];
                        const p = cell?.price ?? null;
                        const isMin = p !== null && r.min_price !== null && p === r.min_price && r.max_price !== r.min_price;
                        const isMax = p !== null && r.max_price !== null && p === r.max_price && r.max_price !== r.min_price;
                        return (
                          <td
                            key={c.code}
                            className={`px-4 py-2.5 text-right tabular-nums ${isMin ? 'text-green-700 font-semibold' : isMax ? 'text-red-600 font-semibold' : 'text-slate-700'}`}
                          >
                            {p !== null ? p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : <span className="text-slate-300">—</span>}
                          </td>
                        );
                      })}
                      <td className="px-4 py-2.5 text-right text-xs">
                        {spread !== null ? <span className={spread > 10 ? 'text-amber-600 font-medium' : 'text-slate-400'}>{spread.toFixed(0)}%</span> : '—'}
                      </td>
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr><td colSpan={gridChannels.length + 3} className="text-center py-12 text-slate-400">
                    {tr ? 'Sonuç yok' : 'No results'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
            <span>{filtered.length.toLocaleString()} {tr ? 'ürün' : 'products'}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 rounded-lg hover:bg-slate-100 disabled:opacity-40">‹</button>
              <span>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 rounded-lg hover:bg-slate-100 disabled:opacity-40">›</button>
            </div>
          </div>
        </div>
      )}

      {/* Modallar */}
      <ChannelImportModal
        isOpen={!!importChannel}
        channel={importChannel}
        country={country}
        onClose={() => setImportChannel(null)}
        onComplete={loadAll}
      />
      <UnmatchedPanel
        isOpen={unmatchedOpen}
        country={country}
        onClose={() => setUnmatchedOpen(false)}
        onResolved={loadAll}
      />
    </div>
  );
};

export default PriceComparisonPage;
